import Logger from "neon-frontend-utils/src/logger";
import Components from "../../../constants/Components";
import NeonHnaCategories from "../../../constants/NeonHnaCategories";
import ResultTypes from "../constants/ResultTypes";
import ScaleTypes from "../constants/ScaleTypes";
import ScoreSections from "../constants/ScoreSections";
import ScoreSubkeys from "../constants/ScoreSubkeys";
import * as actionType from "./../constants/ActionTypes";

const initialState = {
    results: {
        details: {},
        scores: {},
    },
};

/**
 * @returns {null|string} score
 * @param {Object} scoreCollection - key-value object containing scores
 * @param {string} key - string containing key to search for
 * @param {boolean} allowZero - whether zero is a valid value
 */
const pluckSpecificScore = (scoreCollection, key, allowZero = false) => {
    if (
        // eslint-disable-next-line no-undefined
        (scoreCollection[key] !== undefined && scoreCollection[key] !== null) ||
        (scoreCollection[key] === 0 && allowZero)
    ) {
        return scoreCollection[key];
    }

    return null;
};

/**
 * Returns the score for given identifier
 * @param {string} identifier - identifier
 * @param {Object} response - action.response object
 * @param {boolean} asInteger - whether to use whole numbers or not
 * @returns {string|null} score
 */
function returnScore(identifier, response, asInteger = false) {
    if (response.scores[identifier]) {
        const roundingFactor = asInteger ? 0 : 1;
        let foundScore = null;

        for (const key of [ScoreSubkeys.REGULAR, ScoreSubkeys.NORM19]) {
            foundScore = pluckSpecificScore(response.scores[identifier], key);
            if (foundScore !== null) {
                return foundScore.toFixed(roundingFactor);
            }
        }

        foundScore = pluckSpecificScore(response.scores[identifier], ScoreSubkeys.SCORE, true);
        if (foundScore !== null) {
            return foundScore.toFixed(roundingFactor);
        }
    }

    return null;
}

/**
 * Returns the label for given resultType for given category (these do not come with unique identifiers so they cannot
 * be returned by the general returnScore function. Instead they have to be extracted per Type, per Category)
 * @param {string} identifier - identifier for category
 * @param {string} resultType - which result type to extract (label, retestLabel)
 * @param {Object} response - action.response object
 * @returns {string|null} label
 */
function returnLabelForResultsTablesTitle(identifier, resultType, response) {
    if (response.scores[identifier]) {
        if (response.scores[identifier][resultType] !== null) {
            return response.scores[identifier][resultType];
        }
    }
    return null;
}

/**
 * Returns the score for given resultType for given category (these do not come with unique identifiers so they cannot
 * be returned by the general returnScore function. Instead they have to be extracted per Type, per Category)
 * @param {string} identifier - identifier for category
 * @param {string} resultType - which result type to extract (totalCorrect, totalIncorrect, totalOmitted)
 * @param {Object} response - action.response object
 * @param {boolean} integer - whether to use whole numbers or not
 * @returns {string|null} score
 */
function returnResultForCategoryPerType(identifier, resultType, response, integer = false) {
    if (response.scores[identifier]) {
        if (response.scores[identifier][resultType] !== null) {
            const score = response.scores[identifier][resultType];

            if (typeof score === "number") {
                return integer ? score.toFixed(0) : score.toFixed(1);
            }
        }
    }

    return null;
}

/**
 * Returns the new state
 * @param {Object} state - state
 * @param {Object} action - action
 * @returns {Object} new state
 */
export default function resultsReducer(state = initialState, action) {
    const newState = Object.assign({}, state);
    let abstractAbilityRetestFlag = null;
    let verbalAbilityRetestFlag = null;
    let numericalAbilityRetestFlag = null;
    let gMatAbilityRetestFlag = null;

    switch (action.type) {
        case actionType.GET_RESULTS:
            try {
                // reset all intermediate report data
                newState.results = {
                    details: {},
                    retest: null,
                    scores: {},
                };

                // set details
                newState.results.details.name = action.response.details.name || null;
                newState.results.details.status = action.response.details.status || null;
                newState.results.details.educationLevel = action.response.details.educationLevel || null;
                newState.results.details.mboLevel = action.response.details.mboLevel || null;
                newState.results.retest = action.response.retest;

                if (action.response.details.appointmentDate) {
                    newState.results.details.date = action.response.details.appointmentDate.date || null;
                } else {
                    newState.results.details.date = null;
                }

                newState.results.details.organisation = action.response.details.organisation || null;
                newState.results.details.jobFunction = action.response.details.jobFunction || null;

                // set scores if they exist
                if (action.response.scores) {
                    if (action.response.scores.verbalAbility && action.response.scores.verbalAbility.retest) {
                        verbalAbilityRetestFlag = true;
                    }
                    if (action.response.scores.abstractAbility && action.response.scores.abstractAbility.retest) {
                        abstractAbilityRetestFlag = true;
                    }
                    if (action.response.scores.numericalAbility && action.response.scores.numericalAbility.retest) {
                        numericalAbilityRetestFlag = true;
                    }
                    if (action.response.scores.gMatAbility && action.response.scores.gMatAbility.retest) {
                        gMatAbilityRetestFlag = true;
                    }

                    // influencing styles
                    newState.results.scores.influencingStyles = {
                        [NeonHnaCategories.InfluencingStyles.MANIPULATIVE]: returnScore(
                            NeonHnaCategories.InfluencingStyles.MANIPULATIVE,
                            action.response,
                        ),
                        [NeonHnaCategories.InfluencingStyles.ASSERTIVE]: returnScore(
                            NeonHnaCategories.InfluencingStyles.ASSERTIVE,
                            action.response,
                        ),
                        [NeonHnaCategories.InfluencingStyles.STRAIGHTFORWARD]: returnScore(
                            NeonHnaCategories.InfluencingStyles.STRAIGHTFORWARD,
                            action.response,
                        ),
                        [NeonHnaCategories.InfluencingStyles.ANTICIPATING]: returnScore(
                            NeonHnaCategories.InfluencingStyles.ANTICIPATING,
                            action.response,
                        ),
                    };

                    // intellectual capabilities
                    newState.results.scores.intellectualCapabilities = {
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.OMITTED,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_RETEST]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.RETEST,
                                action.response,
                                false,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_RETEST_TOTAL_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_RETEST_TOTAL_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_RETEST_TOTAL_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_OMITTED,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_LABEL]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.LABEL,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_RETEST_LABEL]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE,
                                ResultTypes.LABEL_RETEST,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.OMITTED,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_RETEST]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.RETEST,
                                action.response,
                                false,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_RETEST_TOTAL_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_RETEST_TOTAL_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_RETEST_TOTAL_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_OMITTED,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_LABEL]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.LABEL,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_LABEL_RETEST]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE,
                                ResultTypes.LABEL_RETEST,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.OMITTED,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_RETEST]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.RETEST,
                                action.response,
                                false,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_LABEL]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.LABEL,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_REGULAR]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.REGULAR,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_RETEST_TOTAL_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_RETEST_TOTAL_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_RETEST_TOTAL_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.RETEST_TOTAL_OMITTED,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_LABEL_RETEST]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE,
                                ResultTypes.LABEL_RETEST,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY,
                                ResultTypes.CORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY,
                                ResultTypes.INCORRECT,
                                action.response,
                                true,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_OMITTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY,
                                ResultTypes.OMITTED,
                                action.response,
                                true,
                            ),

                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_LABEL]:
                            returnLabelForResultsTablesTitle(
                                NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY,
                                ResultTypes.LABEL,
                                action.response,
                            ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO1]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO1,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO2]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO2,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO3]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO3,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO4]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_MBO4,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.TOTAL_INTELLIGENCE]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.TOTAL_INTELLIGENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.IntellectualCapabilities.ABSTRACT_INTELLIGENCE_RETEST_FLAG]:
                            abstractAbilityRetestFlag,
                        [NeonHnaCategories.IntellectualCapabilities.NUMERIC_INTELLIGENCE_RETEST_FLAG]:
                            numericalAbilityRetestFlag,
                        [NeonHnaCategories.IntellectualCapabilities.VERBAL_INTELLIGENCE_RETEST_FLAG]:
                            verbalAbilityRetestFlag,
                        [NeonHnaCategories.IntellectualCapabilities.GMAT_ABILITY_RETEST_FLAG]: gMatAbilityRetestFlag,

                        [NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING]: returnScore(
                            NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING,
                            action.response,
                        ),

                        [NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING]: pluckSpecificScore(
                            action.response.scores[NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING],
                            ScoreSubkeys.STANINE,
                        ),

                        [NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING_COUNT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING,
                                ResultTypes.COUNT,
                                action.response,
                                true,
                            ),

                        [NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING_ATTEMPTED]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING,
                                ResultTypes.ATTEMPTED,
                                action.response,
                                true,
                            ),

                        [NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING_CORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING,
                                ResultTypes.CORRECT,
                                action.response,
                                true,
                            ),

                        [NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING_INCORRECT]:
                            returnResultForCategoryPerType(
                                NeonHnaCategories.IntellectualCapabilities.CRITICAL_THINKING,
                                ResultTypes.INCORRECT,
                                action.response,
                                true,
                            ),
                    };

                    // personality
                    newState.results.scores.personality = {
                        [NeonHnaCategories.Personality.EMOTIONAL_STABILITY]: returnScore(
                            NeonHnaCategories.Personality.EMOTIONAL_STABILITY,
                            action.response,
                        ),
                        [NeonHnaCategories.Personality.EXTRAVERSION]: returnScore(
                            NeonHnaCategories.Personality.EXTRAVERSION,
                            action.response,
                        ),
                        [NeonHnaCategories.Personality.CONSCIENTIOUSNESS]: returnScore(
                            NeonHnaCategories.Personality.CONSCIENTIOUSNESS,
                            action.response,
                        ),
                        [NeonHnaCategories.Personality.OPEN_MINDNESS]: returnScore(
                            NeonHnaCategories.Personality.OPEN_MINDNESS,
                            action.response,
                        ),
                        [NeonHnaCategories.Personality.MILDNESS]: returnScore(
                            NeonHnaCategories.Personality.MILDNESS,
                            action.response,
                        ),
                    };

                    // personality - emotional stability sub scales (only for neon scores)
                    newState.results.scores.personalityEmotionalStability = {
                        [NeonHnaCategories.PersonalityEmotionalStability.RELATIONAL_STRESS_TOLERANCE]: returnScore(
                            NeonHnaCategories.PersonalityEmotionalStability.RELATIONAL_STRESS_TOLERANCE,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityEmotionalStability.DOWN_TO_EARTH]: returnScore(
                            NeonHnaCategories.PersonalityEmotionalStability.DOWN_TO_EARTH,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityEmotionalStability.TASK_RELATED_STRESS_TOLERANCE]: returnScore(
                            NeonHnaCategories.PersonalityEmotionalStability.TASK_RELATED_STRESS_TOLERANCE,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityEmotionalStability.OPTIMISM]: returnScore(
                            NeonHnaCategories.PersonalityEmotionalStability.OPTIMISM,
                            action.response,
                        ),
                    };

                    // personality - extraversion sub scales (only for neon scores)
                    newState.results.scores.personalityExtraversion = {
                        [NeonHnaCategories.PersonalityExtraversion.SOCIABILITY]: returnScore(
                            NeonHnaCategories.PersonalityExtraversion.SOCIABILITY,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityExtraversion.DOMINANCE]: returnScore(
                            NeonHnaCategories.PersonalityExtraversion.DOMINANCE,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityExtraversion.ENERGY]: returnScore(
                            NeonHnaCategories.PersonalityExtraversion.ENERGY,
                            action.response,
                        ),
                    };

                    // personality - conscientiousness sub scales (only for neon scores)
                    newState.results.scores.personalityConscientiousness = {
                        [NeonHnaCategories.PersonalityConscientiousness.WORKING_SYSTEMATICALLY]: returnScore(
                            NeonHnaCategories.PersonalityConscientiousness.WORKING_SYSTEMATICALLY,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityConscientiousness.RESPONSIBILITY]: returnScore(
                            NeonHnaCategories.PersonalityConscientiousness.RESPONSIBILITY,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityConscientiousness.SELF_DISCIPLINE]: returnScore(
                            NeonHnaCategories.PersonalityConscientiousness.SELF_DISCIPLINE,
                            action.response,
                        ),
                    };

                    // personality - open-mindness sub scales (only for neon scores)
                    newState.results.scores.personalityOpenMindness = {
                        [NeonHnaCategories.PersonalityOpenMindness.IMAGINATION]: returnScore(
                            NeonHnaCategories.PersonalityOpenMindness.IMAGINATION,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityOpenMindness.INDEPENDENT_THINKING]: returnScore(
                            NeonHnaCategories.PersonalityOpenMindness.INDEPENDENT_THINKING,
                            action.response,
                        ),
                    };

                    // personality - friendliness sub scales (only for neon scores)
                    newState.results.scores.personalityFriendliness = {
                        [NeonHnaCategories.PersonalityFriendliness.HELPFULNESS]: returnScore(
                            NeonHnaCategories.PersonalityFriendliness.HELPFULNESS,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityFriendliness.TRUST]: returnScore(
                            NeonHnaCategories.PersonalityFriendliness.TRUST,
                            action.response,
                        ),
                    };

                    // personality plus scales (only for neon scores)
                    newState.results.scores.personalityPlus = {
                        [NeonHnaCategories.PersonalityPlus.MATERIALISM]: returnScore(
                            NeonHnaCategories.PersonalityPlus.MATERIALISM,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityPlus.PRIDE]: returnScore(
                            NeonHnaCategories.PersonalityPlus.PRIDE,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityPlus.SINCERITY]: returnScore(
                            NeonHnaCategories.PersonalityPlus.SINCERITY,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityPlus.INDULGENCE]: returnScore(
                            NeonHnaCategories.PersonalityPlus.INDULGENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.PersonalityPlus.CHANGE_ORIENTATED]: returnScore(
                            NeonHnaCategories.PersonalityPlus.CHANGE_ORIENTATED,
                            action.response,
                        ),
                    };

                    // working styles
                    newState.results.scores.workingStyles = {
                        [NeonHnaCategories.WorkingStyles.SUCCESS_ACHIEVEMENT]: returnScore(
                            NeonHnaCategories.WorkingStyles.SUCCESS_ACHIEVEMENT,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.QUALITY_ACHIEVEMENT]: returnScore(
                            NeonHnaCategories.WorkingStyles.QUALITY_ACHIEVEMENT,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.DEVELOPMENT_ORIENTATION]: returnScore(
                            NeonHnaCategories.WorkingStyles.DEVELOPMENT_ORIENTATION,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.PERFORMANCE_UNCERTAINTY]: returnScore(
                            NeonHnaCategories.WorkingStyles.PERFORMANCE_UNCERTAINTY,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.LOOKING_FOR_CHALLENGES]: returnScore(
                            NeonHnaCategories.WorkingStyles.LOOKING_FOR_CHALLENGES,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.DECISIVENESS]: returnScore(
                            NeonHnaCategories.WorkingStyles.DECISIVENESS,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.GOAL_ORIENTED]: returnScore(
                            NeonHnaCategories.WorkingStyles.GOAL_ORIENTED,
                            action.response,
                        ),
                        [NeonHnaCategories.WorkingStyles.TAKING_RISKS]: returnScore(
                            NeonHnaCategories.WorkingStyles.TAKING_RISKS,
                            action.response,
                        ),
                    };

                    // motives
                    newState.results.scores.motives = {
                        [NeonHnaCategories.Motives.PERFORMANCE_MOTIVATION]: returnScore(
                            NeonHnaCategories.Motives.PERFORMANCE_MOTIVATION,
                            action.response,
                        ),
                        [NeonHnaCategories.Motives.NEED_TO_INFLUENCE]: returnScore(
                            NeonHnaCategories.Motives.NEED_TO_INFLUENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.Motives.NEED_FOR_ACCEPTANCE]: returnScore(
                            NeonHnaCategories.Motives.NEED_FOR_ACCEPTANCE,
                            action.response,
                        ),
                        [NeonHnaCategories.Motives.NEED_FOR_AUTONOMY]: returnScore(
                            NeonHnaCategories.Motives.NEED_FOR_AUTONOMY,
                            action.response,
                        ),
                    };

                    // values
                    newState.results.scores.valuesQuestionnaire = {
                        [NeonHnaCategories.ValuesQuestionnaire.SUPPORT_AND_RECOGNITION]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.SUPPORT_AND_RECOGNITION,
                            action.response,
                        ),
                        [NeonHnaCategories.ValuesQuestionnaire.CONFORMITY]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.CONFORMITY,
                            action.response,
                        ),
                        [NeonHnaCategories.ValuesQuestionnaire.INDEPENDENCE]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.INDEPENDENCE,
                            action.response,
                        ),
                        [NeonHnaCategories.ValuesQuestionnaire.ALTRUISM]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.ALTRUISM,
                            action.response,
                        ),
                        [NeonHnaCategories.ValuesQuestionnaire.LEADERSHIP]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.LEADERSHIP,
                            action.response,
                        ),
                        [NeonHnaCategories.ValuesQuestionnaire.STRUCTURE]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.STRUCTURE,
                            action.response,
                        ),
                        [NeonHnaCategories.ValuesQuestionnaire.VARIETY]: returnScore(
                            NeonHnaCategories.ValuesQuestionnaire.VARIETY,
                            action.response,
                        ),
                    };

                    // toBe
                    newState.results.scores.toBe = {
                        [NeonHnaCategories.ToBe.TO_BE_GO_GETTER]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_GO_GETTER,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_GO_GETTER, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_CONTROLLER]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_CONTROLLER,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_CONTROLLER, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_INNOVATOR]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_INNOVATOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_INNOVATOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_EXPERT]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_EXPERT,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_EXPERT, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_COORDINATOR]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_COORDINATOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_COORDINATOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_INSPIRATOR]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_INSPIRATOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_INSPIRATOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_CONNECTOR]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_CONNECTOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_CONNECTOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToBe.TO_BE_ENTREPRENEUR]: returnScore(
                            NeonHnaCategories.ToBe.TO_BE_ENTREPRENEUR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToBe.TO_BE_ENTREPRENEUR, action.response, true)}%`
                            : null,
                    };

                    // toWant
                    newState.results.scores.toWant = {
                        [NeonHnaCategories.ToWant.TO_WANT_GO_GETTER]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_GO_GETTER,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_GO_GETTER, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_CONTROLLER]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_CONTROLLER,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_CONTROLLER, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_INNOVATOR]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_INNOVATOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_INNOVATOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_EXPERT]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_EXPERT,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_EXPERT, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_COORDINATOR]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_COORDINATOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_COORDINATOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_INSPIRATOR]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_INSPIRATOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_INSPIRATOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_CONNECTOR]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_CONNECTOR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_CONNECTOR, action.response, true)}%`
                            : null,
                        [NeonHnaCategories.ToWant.TO_WANT_ENTREPRENEUR]: returnScore(
                            NeonHnaCategories.ToWant.TO_WANT_ENTREPRENEUR,
                            action.response,
                        )
                            ? `${returnScore(NeonHnaCategories.ToWant.TO_WANT_ENTREPRENEUR, action.response, true)}%`
                            : null,
                    };

                    // fit
                    newState.results.scores.fit = {
                        [NeonHnaCategories.Fit.FIT_MATCH]: returnScore(NeonHnaCategories.Fit.FIT_MATCH, action.response)
                            ? `${returnScore(NeonHnaCategories.Fit.FIT_MATCH, action.response, true)}%`
                            : null,
                    };
                }

                // answerPattern
                const answerPatternScoreSection = action.response.reportSectionScores[ScoreSections.ANSWERPATTERN]
                    ? action.response.reportSectionScores[ScoreSections.ANSWERPATTERN]
                    : null;

                if (answerPatternScoreSection) {
                    if (answerPatternScoreSection.completed && answerPatternScoreSection.scores) {
                        const answerPatternScores = {};

                        answerPatternScoreSection.scores.forEach((score) => {
                            answerPatternScores[score.handle] = {
                                label: score.name,
                                order: score.order,
                                score: score.score,
                                stanine: score.stanine || null,
                            };
                        });

                        newState.results.scores.answerPattern = answerPatternScores;
                    }
                }

                // hexit
                const hexitScoreSection = action.response.reportSectionScores[ScoreSections.HEXIT]
                    ? action.response.reportSectionScores[ScoreSections.HEXIT]
                    : null;

                if (hexitScoreSection) {
                    if (hexitScoreSection.completed && hexitScoreSection.scores) {
                        const hexitScores = {};

                        hexitScoreSection.scores.forEach((score) => {
                            hexitScores[score.handle] = {
                                group: score.group || null,
                                order: score.order || null,
                                riskEvaluation: score.evaluation || null,
                                riskIndication: score.riskAssessment || null,
                                score: score.score || null,
                                title: score.handle || null,
                            };
                        });

                        newState.results.scores.hexit = hexitScores;
                    }
                }

                // dilemmaTest
                const dilemmaScoreSection = action.response.reportSectionScores[ScoreSections.DILEMMATEST]
                    ? action.response.reportSectionScores[ScoreSections.DILEMMATEST]
                    : null;

                if (dilemmaScoreSection) {
                    if (dilemmaScoreSection.completed && dilemmaScoreSection.scores) {
                        const dilemmaScores = {
                            mainScales: {},
                            subScales: {},
                        };

                        dilemmaScoreSection.scores.forEach((score) => {
                            const name = score.name;
                            const mappedScore = {
                                evaluation: score.evaluation,
                                handle: score.handle,
                                name,
                                parentScale: score.parentScale,
                                range: score.range,
                                scaleType: score.scaleType,
                                score: score.score,
                            };

                            if (score.scaleType === ScaleTypes.MAIN) {
                                dilemmaScores.mainScales[name] = mappedScore;
                            } else if (score.scaleType === ScaleTypes.SUB) {
                                // add sub scale (per case)
                                if (!dilemmaScores.subScales[name]) {
                                    dilemmaScores.subScales[name] = {
                                        name,
                                    };
                                }

                                dilemmaScores.subScales[name][score.parentScale] = mappedScore;
                            }
                        });

                        newState.results.scores.dilemmaTest = dilemmaScores;
                    }
                }

                // personalityBuildingDocks
                const personalityBuildingDocksSection = action.response.reportSectionScores[
                    ScoreSections.PERSONALITYBUILDINGDOCKS
                ]
                    ? action.response.reportSectionScores[ScoreSections.PERSONALITYBUILDINGDOCKS]
                    : null;

                if (personalityBuildingDocksSection) {
                    if (personalityBuildingDocksSection.completed && personalityBuildingDocksSection.scores) {
                        const personalityBuildingDocksScores = {};

                        personalityBuildingDocksSection.scores.forEach((score) => {
                            personalityBuildingDocksScores[score.handle] = `${score.score}%`;
                        });

                        newState.results.scores.personalityBuildingDocks = personalityBuildingDocksScores;
                    }
                }

                // risk indication BuildingDocks
                const riskIndicationBuildingDocksSection = action.response.reportSectionScores[
                    ScoreSections.RISKINDICATIONBUILDINGDOCKS
                ]
                    ? action.response.reportSectionScores[ScoreSections.RISKINDICATIONBUILDINGDOCKS]
                    : null;

                if (riskIndicationBuildingDocksSection) {
                    if (riskIndicationBuildingDocksSection.completed && riskIndicationBuildingDocksSection.scores) {
                        const riskIndicationBuildingDocksScores = {};

                        riskIndicationBuildingDocksSection.scores.forEach((score) => {
                            riskIndicationBuildingDocksScores[score.handle] = score.score;
                        });

                        newState.results.scores.riskIndicationBuildingDocks = riskIndicationBuildingDocksScores;
                    }
                }

                // set retest indication if it exists
                if (action.response.retest) {
                    newState.results.retest = action.response.retest;
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.INTERMEDIATE_REPORT,
                    error,
                    message: "Exception in intermediate report reducer GET_INTERMEDIATE_REPORT",
                });
            }
            break;
        default:
            return state;
    }

    return newState;
}
